/* oxlint-disable no-console -- console/OTel observability layer */
import type { TelemetryLocalStorePort } from '@core/domain/ports/driven/TelemetryLocalStorePort.ts';
import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import {
  type StructuredUserSnapshot,
  StructuredLogger,
} from '../../../logging/infrastructure/StructuredLogger.ts';
import {
  emitMetricCounter,
  emitMetricHistogram,
  ensureBrowserTracing,
  startDiagnosticTraceSpan,
} from './openTelemetrySetup.ts';

/** {@link TelemetryPort}: structured JSON logs to the console and optional local SQLite persistence. */
export class ObservabilityTelemetryAdapter implements TelemetryPort {
  private readonly logger = new StructuredLogger();
  private userSnapshot: StructuredUserSnapshot = null;

  constructor(private readonly localStore?: TelemetryLocalStorePort | null) {
    ensureBrowserTracing();
  }
  /** Log info. */


  logInfo(message: string, context?: Record<string, unknown>): void {
    this.logger.info(message, context);
    try {
      this.localStore?.writeLog('info', message, context, this.userSnapshot);
    } catch {
      /* */
    }
  }
  /** Log error. */


  logError(error: Error | string, context?: Record<string, unknown>): void {
    this.logger.error(error, context);
    const errName = error instanceof Error ? error.name : 'string';
    emitMetricCounter('admin.frontend.logged_errors_total', 1, {
      error_class: errName,
    });
    try {
      const body = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
      this.localStore?.writeLog('error', body, context, this.userSnapshot);
    } catch {
      /* */
    }
  }
  /** Track event. */


  trackEvent(eventName: string, payload?: Record<string, unknown>): void {
    this.logger.event(eventName, payload);
    emitMetricCounter('admin.frontend.track_events_total', 1, { event: eventName });
    let correlationId: string | undefined;
    let storePayload = payload;
    if (payload && typeof payload.correlationId === 'string') {
      correlationId = payload.correlationId;
      const { correlationId: _c, ...rest } = payload;
      storePayload = Object.keys(rest).length ? rest : undefined;
    }
    try {
      this.localStore?.writeEvent(eventName, storePayload, this.userSnapshot, {
        correlationId,
        pageUrl: globalThis.location?.href ?? null,
      });
    } catch {
      /* */
    }
  }
  /** Sets user context. */


  setUserContext(user: { id: string; role?: string; [key: string]: unknown } | null): void {
    this.logger.setUserContext(user);
    this.userSnapshot = user
      ? { id: user.id, email: user.email as string | undefined, role: user.role }
      : null;
  }
  /** Log http request. */


  logHttpRequest(meta: { correlationId: string; method: string; url: string }): void {
    this.logger.httpRequest(meta);
    try {
      this.localStore?.writeHttpRequest(meta, this.userSnapshot);
    } catch {
      /* */
    }
  }
  /** Log http response. */


  logHttpResponse(meta: { correlationId: string; status: number; durationMs: number; url: string }): void {
    this.logger.httpResponse(meta);
    try {
      this.localStore?.writeHttpResponse(meta, this.userSnapshot);
    } catch {
      /* */
    }
  }
  /** Start trace. */


  startTrace(
    spanName: string,
    options?: { correlationId?: string }
  ): { end: () => void; fail: (err: Error) => void } {
    const inner = startDiagnosticTraceSpan(spanName);
    const store = this.localStore;
    const t0 = globalThis.performance?.now?.() ?? Date.now();
    const traceCid = options?.correlationId;
    const userSnapshot = this.userSnapshot;
    const pageUrl = globalThis.location?.href ?? null;
    return {
      end: () => {
        inner.end();
        try {
          const ms = Math.round((globalThis.performance?.now?.() ?? Date.now()) - t0);
          store?.writeTrace(spanName, true, ms, {
            correlationId: traceCid,
            pageUrl,
            user: userSnapshot,
          });
        } catch {
          /* */
        }
      },
      fail: (err: Error) => {
        inner.fail(err);
        try {
          const ms = Math.round((globalThis.performance?.now?.() ?? Date.now()) - t0);
          store?.writeTrace(spanName, false, ms, {
            errorMessage: err.message,
            correlationId: traceCid,
            pageUrl,
            user: userSnapshot,
          });
        } catch {
          /* */
        }
      },
    };
  }
  /** Record metric counter. */


  recordMetricCounter(
    name: string,
    value = 1,
    attributes?: Record<string, string | number | boolean>
  ): void {
    emitMetricCounter(name, value, attributes);
    try {
      this.localStore?.writeMetric('counter', name, value, attributes, this.userSnapshot);
    } catch {
      /* */
    }
  }
  /** Record metric histogram. */


  recordMetricHistogram(
    name: string,
    value: number,
    attributes?: Record<string, string | number | boolean>
  ): void {
    emitMetricHistogram(name, value, attributes);
    try {
      this.localStore?.writeMetric('histogram', name, value, attributes, this.userSnapshot);
    } catch {
      /* */
    }
  }
}
